import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import HelpModal from '../HelpModal';
import { FaPlay, FaPause, FaVolumeUp, FaVolumeMute } from 'react-icons/fa';
import HEXASLogo from '../assets/Logos/HEXASLogo.jpg';
import { useEffect } from 'react';
import { useTextHighlight } from '../utils/highlightUtils';

const Class15GuidedPractice02 = () => {
  const navigate = useNavigate();

  const highlightRef = useRef(null);
  useTextHighlight(highlightRef);

  const [showHelp, setShowHelp] = useState(false);

  const questionRefs = useRef([]);
  const rightColRef = useRef(null);

  // Make sure your answers state exists
  const [answers, setAnswers] = useState(Array(20).fill(''));
  const [currentQuestion, setCurrentQuestion] = useState(14);
  const onNext = () => {
    setCurrentQuestion(prev => Math.min(prev + 1, 19));
  };

  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(100 * 60); // 30 minutes in seconds
  const [showSeconds, setShowSeconds] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 0) {
          clearInterval(timer);
          navigate('/');  // Redirect to home
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (showSeconds) {
      return `${mins} : ${secs.toString().padStart(2, '0')} remaining`;
    }
    return `${mins} minutes remaining`;
  };

  const handleStartTest = () => {
    navigate(`/guided-practice-01-form`);
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (!audioRef.current) return;
    setDuration(audioRef.current.duration);
  };

  const handleSeek = (e) => {
    if (!audioRef.current) return;
    const time = parseFloat(e.target.value);
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  };

  const handleVolumeChange = (e) => {
    if (!audioRef.current) return;
    const vol = parseFloat(e.target.value);
    setVolume(vol);
    audioRef.current.volume = vol;
    setIsMuted(vol === 0);
  };

  const handleMute = () => {
    if (!audioRef.current) return;
    if (isMuted) {
      audioRef.current.muted = false;
      setIsMuted(false);
      audioRef.current.volume = volume;
    } else {
      audioRef.current.muted = true;
      setIsMuted(true);
    }
  };

  const GreenCheck = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
    >
      <path
        d="M7 11L9 13.5L16 7"
        stroke="#7DD77D"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );


  return (
    <div className="min-h-screen bg-white flex flex-col font-[Arial]">
      {/* Help Modal */}
      <HelpModal
        isOpen={showHelp}
        onClose={() => setShowHelp(false)}
      />
      {/* Top Bar (fixed) */}
      <div className="sticky top-0 z-30 bg-white">
        <div className="relative h-[62px] bg-[#F4C3C3] border-b border-black flex items-center justify-between px-4">
          {/* Left + Center combined on left side */}
          <div className="flex flex-col justify-center space-y-0.5 pl-6">
            <span className="text-[14px] text-left text-black font-bold leading-tight">
              HEXA'S Education
            </span>
            <div className="flex items-center space-x-6 text-[14px] text-black font-medium">
              <span
                onMouseEnter={() => setShowSeconds(true)}
                onMouseLeave={() => setShowSeconds(false)}
                className="cursor-pointer"
              >
                {formatTime(timeRemaining)}
              </span>
            </div>
          </div>
          {/* Right: Icons and Finish Button */}
          <div className="flex items-center space-x-5">
            <button
              onClick={() => navigate('/')} 
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Back Home
            </button>
            <button
              onClick={() => setShowHelp(true)}
              className="text-black border border-black rounded-md px-4 py-1 text-[14px]  hover:bg-gray-100"
            >
              Finish test
            </button>
            <div className="flex items-center space-x-8 pl-2 pr-2">
              {/* WiFi Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-9 h-9 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M17.778 8.222c-4.296-4.296-11.26-4.296-15.556 0A1 1 0 010.808 6.808c5.076-5.077 13.308-5.077 18.384 0a1 1 0 01-1.414 1.414zM14.95 11.05a7 7 0 00-9.9 0 1 1 0 01-1.414-1.414 9 9 0 0112.728 0 1 1 0 01-1.414 1.414zM12.12 13.88a3 3 0 00-4.24 0 1 1 0 01-1.415-1.415 5 5 0 717.07 0 1 1 0 01-1.415 1.415zM9 16a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Notification Bell Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                </svg>
              </div>
              {/* Hamburger Menu Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              {/* Pen/Edit Icon */}
              <div className="w-10 h-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
              </div>
            </div>
          </div>
        </div>
        {/* Title Bar (fixed under top bar) */}
        <div className="relative bg-[#F4F7FA] shadow-sm border border-black rounded-lg mx-12 mt-4 mb-0 sticky top-[32px] z-20 flex items-center justify-between">
          {/* Left: Part Title */}
          <div className="flex-1 px-4 py-2 text-left">
            <h2 className="text-[23px] text-black">Part 1</h2>
            <h1 className="text-[18px] text-black">HEXA'S Reading Course Book</h1>
          </div>
          {/* Center: Absolutely Positioned Logo */}
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src={HEXASLogo}
              alt="HEXA'S Logo"
              className="h-[50px] object-contain"
            />
          </div>
          {/* Right: Empty space for consistency */}
          <div className="flex items-center justify-end w-[300px] pr-4">
            {/* Empty space - no audio player for reading */}
          </div>
        </div>
      </div>
      {/* Main content (scrollable) */}
      <div className="flex-1 flex flex-col items-center justify-start w-full">
        <div ref={highlightRef}
          className="flex flex-row w-full overflow-hidden -mb-16 mt-2 px-12 pb-20"
          style={{ height: 'calc(100vh - 180px)' }}
        >
          {/* Left: Reading Passage */}
          <div className="bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 w-[49%] h-full max-h-full overflow-y-auto">
            <div className="mb-8">
              <h2 className="text-[16px] font-bold mb-4">I contain multitudes</h2>

              {/* First paragraph - centered, italic */}
              <p className="text-[14px] italic font-semibold mb-4 text-center">
                Wendy Moore reviews Ed Yong’s book about microbes
              </p>

              {/* Remaining paragraphs - left aligned */}
              <div className="text-[14px] space-y-4 text-left">
                <p>
                  Microbes, most of them bacteria, have populated this planet since long before animal life developed and they will outlive us. Invisible to the naked eye, they are ubiquitous. They inhabit the soil, air, rocks and water and are present within every form of life, from seaweed and coral to dogs and humans. And, as Yong explains in his utterly absorbing and hugely important book we mess with them at our peril.
                </p>
                <p>
                  Every species has its own colony of microbes, called a ‘microbiome’, and these microbes vary not only between species but also between individuals and within different parts of each individual. What is amazing is that while the number of human cells in the average person is about 30 trillion, the number of microbial ones is higher – about 39 trillion. At best, Yong informs us, we are only 50 per cent human. Indeed, some scientists even suggest we should think of each species and its microbes as a single unit, dubbed a ‘holobiont’.
                </p>
                <p>
                  In each human there are microbes that live only in the stomach, the mouth or the armpit and by and large they do so peacefully. So ‘bad’ microbes are just microbes out of context. Microbes that sit contentedly in the human gut (where there are more microbes than there are stars in the galaxy) can become deadly if they find their way into the bloodstream. These communities are constantly changing too. The right-hand shares just one sixth of its microbes with the left hand. And, of course, we are surrounded by microbes. Every time we eat, we swallow a million microbes in each gram of food; we are continually swapping microbes with other humans, pets and the world at large.
                </p>
                <p>
                  It’s a fascinating topic and Yong, a young British science journalist, is an extraordinarily adept guide. Writing with lightness and panache, he has a knack of explaining complex science in terms that are both easy to understand and totally enthralling. Yong is on a mission. Leading us gently by the hand, he takes us into the world of microbes – a bizarre, alien planet – in a bid to persuade us to love them as much as he does. By the end, we do.
                </p>
                <p>
                  For most of human history we had no idea that microbes existed. The first man to see these extraordinarily potent creatures was a Dutch lens-maker called Antony van Leeuwenhoek in the 1670s. Using microscopes of his own design that could magnify up to 270 times, he examined a drop of water from a nearby lake and found it teeming with tiny creatures he called ‘animalcules’. It wasn’t until nearly two hundred years later that the research of French biologist Louis Pasteur indicated that some microbes caused disease. It was Pasteur’s ‘germ theory’ that gave bacteria the poor image that endures today.
                </p>
                <p>
                  Yong’s book is in many ways a plea for microbial tolerance, pointing out that while fewer than one hundred species of bacteria bring disease, many thousands more play a vital role in maintaining our health. The book also acknowledges that our attitude towards bacteria is not a simple one. We tend to see the dangers posed by bacteria, yet at the same time we are sold yoghurts and drinks that supposedly nurture ‘friendly’ bacteria. In reality, says Yong, bacteria should not be viewed as either friends or foes, villains or heroes. Instead, we should realise we have a symbiotic relationship, that can be mutually beneficial or mutually destructive.
                </p>
                <p>
                  What then do these millions of organisms do? The answer is pretty much everything. New research is now unravelling the ways in which bacteria aid digestion, regulate our immune systems, eliminate toxins, produce vitamins, affect our behaviour and even combat obesity. ‘They actually help us become who we are,’ says Yong. But we are facing a growing problem. Our obsession with hygiene, our overuse of antibiotics and our unhealthy, low-fibre diets are disrupting the bacterial balance and may be responsible for soaring rates of allergies and immune problems, such as inflammatory bowel disease (IBD).
                </p>
                <p>
                  The most recent research actually turns accepted norms upside down. For example, there are studies indicating that the excessive use of household detergents and antibacterial products actually destroys the microbes that normally keep the more dangerous germs at bay. Other studies show that keeping a dog as a pet gives children early exposure to a diverse range of bacteria, which may help protect them against allergies later.
                </p>
                <p>
                  The readers of Yong’s book must be prepared for a decidedly unglamorous world. Among the less appealing case studies is one about a fungus that is wiping out entire populations of frogs and that can be halted by a rare microbial bacterium. Another is about squid that carry luminescent bacteria that protect them against predators. However, if you can overcome your distaste for some of the investigations, the reasons for Yong’s enthusiasm become clear. The microbial world is a place of wonder. Already, in an attempt to stop mosquitoes spreading dengue fever – a disease that infects 400 million people a year – mosquitoes are being loaded with a bacterium to block the disease. In the future, our ability to manipulate microbes means we could construct buildings with useful microbes built into their walls to fight off infections. Just imagine a neonatal hospital ward coated in a specially mixed cocktail of microbes so that babies get the best start in life.
                </p>
              </div>
            </div>
          </div>




          {/* Right: Questions */}
          <div
            ref={rightColRef}
            className="w-[50%] flex flex-col items-start bg-[#F4F7FA] rounded-md shadow border border-gray-300 p-6 h-full max-h-full overflow-y-auto"
          >
            {/* Questions 14–16 */}
            <div className="mb-10 w-full">
              <h3 className="text-[15px] font-bold mb-4 text-left">Questions 14 – 16</h3>
              <p className="text-[14px] mb-6 text-left">
                Choose the correct answer.
              </p>

              <div className="space-y-2">
                {[
                  {
                    question: "What point does the writer make about microbes in the first paragraph?",
                    options: [
                      "They adapt quickly to their environment.",
                      "The risk they pose has been exaggerated.",
                      "They are more plentiful in animal life than plant life.",
                      "They will continue to exist for longer than the human race.",
                    ],
                  },
                  {
                    question:
                      "In the second paragraph, the writer is impressed by the fact that",
                    options: [
                      "each species tends to have vastly different microbes.",
                      "some parts of the body contain relatively few microbes.",
                      "the average individual has more microbial cells than human ones.",
                      "scientists have limited understanding of how microbial cells behave.",
                    ],
                  },
                  {
                    question: "What is the writer doing in the fifth paragraph?",
                    options: [
                      "explaining how a discovery was made",
                      "comparing scientists’ theories about microbes",
                      "describing confusion among scientists",
                      "giving details of how microbes cause disease",
                    ],
                  },
                ].map((item, idx) => {
                  const questionNum = 14 + idx;
                  const isOpen = currentQuestion === questionNum;

                  return (
                    <div
                      key={questionNum}
                      className="mb-2 rounded bg-[#f5f8fc]"
                    >
                      {/* Question Header */}
                      <div
                        className="px-3 py-1.5 cursor-pointer"
                        onClick={() =>
                          setCurrentQuestion(isOpen ? null : questionNum)
                        }
                      >
                        <div className="flex items-start gap-x-3">
                          <span className="font-semibold text-[14px]">
                            {questionNum}
                          </span>
                          <span className="text-[14px]">{item.question}</span>
                        </div>
                      </div>

                      {/* Answer Options */}
                      {isOpen && (
                        <div className="mt-1 mb-2 px-4 py-2 pl-6 flex flex-col gap-2 bg-[#D7DFEC] rounded-b-md">
                          {item.options.map((opt, i) => (
                            <label
                              key={i}
                              className="flex items-center text-[13px] cursor-pointer"
                            >
                              <input
                                type="radio"
                                name={`q${questionNum}`}
                                value={opt}
                                checked={answers[questionNum - 1] === opt}
                                onChange={() => {
                                  const newAnswers = [...answers];
                                  newAnswers[questionNum - 1] = opt;
                                  setAnswers(newAnswers);
                                }}
                                className="mr-2 accent-blue-600"
                                onClick={(e) => e.stopPropagation()}
                              />
                              {opt}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>








        </div>
      </div>

      {/* Bottom Navigation (fixed footer) */}
      <div className="sticky bottom-0 left-0 w-full bg-[#D7DFEC] border-t border-gray-300 z-30 flex items-center justify-between px-4 py-3">
        {/* Left: Question Buttons */}
        <div className="flex items-center space-x-1">
          {[14, 15, 16].map((questionNumber) => (
            <button
              key={questionNumber}
              onClick={() => setCurrentQuestion(questionNumber)}
              className={`w-7 h-7 mx-0.5 rounded text-[12px] font-bold flex items-center justify-center ${currentQuestion === questionNumber
                  ? 'bg-blue-500 text-white'
                  : 'bg-black text-white'
                }`}
            >
              {questionNumber}
            </button>
          ))}
        </div>

        {/* Right: Arrows */}
        <div className="flex items-center space-x-2">
          {/* Backward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.max(14, prev - 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 14 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Previous Question"
            disabled={currentQuestion === 14}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Forward Arrow */}
          <button
            onClick={() => {
              setCurrentQuestion((prev) => Math.min(16, prev + 1));
            }}
            className={`w-7 h-7 flex items-center justify-center rounded-full ${currentQuestion === 16 ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500'
              } text-white`}
            title="Next Question"
            disabled={currentQuestion === 16}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              className="w-4 h-4"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>








    </div>
  );
};

export default Class15GuidedPractice02;
